import type { OrderStatus } from '@mypoz/shared';
import { ORDER_STAGES, orderStageIndex } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';

export interface OrderProgressProps {
  status: OrderStatus;
}

/**
 * Five dots and an n/5, rather than a full-width bar.
 *
 * A bar reads as percent complete, which invites "how long until the next twenty percent?" — and
 * these five stages are neither equal in length nor a proportion of anything. Discrete dots say
 * "step two of five" and claim nothing further.
 *
 * A cancelled order renders nothing at all: it did not stall at some point along the track, it
 * left it, and lighting up two of five dots would suggest it is still moving. An `exception` order
 * is the same case for the opposite reason — it has stopped where it stands, and `orderStageIndex`
 * returns 0 for both.
 */
export function OrderProgress({ status }: OrderProgressProps) {
  const { t } = useTranslation();
  const fmt = useFormat();

  const index = orderStageIndex(status);
  if (index === 0) return null;

  return (
    <span
      role="img"
      aria-label={t('orders.card.progressLabel', {
        index: fmt.number(index),
        total: fmt.number(ORDER_STAGES.length),
        status: t(`orders.status.${status}`),
      })}
      className="inline-flex items-center gap-2"
    >
      {/* Hidden from assistive tech: the label above already says it, in words. */}
      <span aria-hidden="true" className="flex items-center gap-1">
        {ORDER_STAGES.map((stage, position) => (
          <span
            key={stage}
            className={cn('size-1.5 rounded-full', position < index ? 'bg-brand' : 'bg-line')}
          />
        ))}
      </span>
      <span aria-hidden="true" className="text-xs text-muted tabular-nums">
        {fmt.number(index)}/{fmt.number(ORDER_STAGES.length)}
      </span>
    </span>
  );
}
